import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { chatApi } from "../api/chat";
import { useChatChannelFilters } from "../hooks/useChatChannelFilters";
import { INFO_CHAT_CHANNEL, useGameStore } from "../store/useGameStore";
import type { ChatChannel, ChatMessage } from "../types";
import { ModalBackdrop } from "./ModalBackdrop";

const POLL_MS_STRIP = 12000;
const POLL_MS_OPEN = 5000;
const STRIP_LINES = 2;
const WRITABLE_CHANNEL_CODES = new Set(["general", "guild"]);
const GUILD_CHAT_CHANNEL = "guild";

function messageNameClass(channelCode: string): string {
  if (channelCode === INFO_CHAT_CHANNEL) return "text-amber-300";
  if (channelCode === GUILD_CHAT_CHANNEL) return "text-emerald-400";
  return "text-kingdom-glow";
}

function messageBodyClass(channelCode: string): string {
  if (channelCode === GUILD_CHAT_CHANNEL) return "text-emerald-300";
  return "text-gray-200";
}

function showChannelTag(channelCode: string): boolean {
  return channelCode !== GUILD_CHAT_CHANNEL;
}

function messageTagClass(channelCode: string): string {
  if (channelCode === GUILD_CHAT_CHANNEL) return "text-emerald-500/80";
  return "text-gray-500";
}

function mergeChatMessages(apiMessages: ChatMessage[], infoMessages: ChatMessage[], showInfo: boolean): ChatMessage[] {
  const merged = showInfo ? [...apiMessages, ...infoMessages] : [...apiMessages];
  merged.sort((a, b) => {
    const byTime = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    if (byTime !== 0) return byTime;
    return a.id - b.id;
  });
  return merged.slice(-120);
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export function ChatPanel() {
  const { user, infoChatMessages } = useGameStore();
  const [channels, setChannels] = useState<ChatChannel[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [composeChannel, setComposeChannel] = useState<string>("general");
  const listRef = useRef<HTMLDivElement>(null);
  const lastMessageIdRef = useRef<number | undefined>(undefined);
  const openRef = useRef(open);
  const activeChannelCodesRef = useRef<string[]>([]);
  const pollGenerationRef = useRef(0);
  const inFlightRef = useRef(false);

  const channelCodes = channels.map((c) => c.code);
  const { enabledCodes, activeChannelCodes, allEnabled, toggleChannel, toggleAll } =
    useChatChannelFilters(channelCodes);

  openRef.current = open;
  activeChannelCodesRef.current = activeChannelCodes;

  const pollChannelCodes = useMemo(
    () => activeChannelCodes.filter((code) => code !== INFO_CHAT_CHANNEL),
    [activeChannelCodes],
  );
  const showInfoChannel = enabledCodes.has(INFO_CHAT_CHANNEL);
  const displayMessages = useMemo(
    () => mergeChatMessages(messages, infoChatMessages, showInfoChannel),
    [messages, infoChatMessages, showInfoChannel],
  );
  const writableChannels = useMemo(
    () => channels.filter((c) => WRITABLE_CHANNEL_CODES.has(c.code) && activeChannelCodes.includes(c.code)),
    [channels, activeChannelCodes],
  );

  const loadChannels = useCallback(async () => {
    try {
      const data = await chatApi.getChannels();
      setChannels(data);
      if (data.length > 0 && !data.some((c) => c.code === composeChannel)) {
        setComposeChannel(data[0].code);
      }
    } catch {
      // Non-fatal — chat strip stays hidden until channels load
    }
  }, [composeChannel]);

  useEffect(() => {
    if (!user) return;
    void loadChannels();
  }, [user, loadChannels]);

  useEffect(() => {
    if (open && user) void loadChannels();
  }, [open, user, loadChannels]);

  useEffect(() => {
    if (!user || channels.length === 0) return;

    const generation = ++pollGenerationRef.current;
    let timer: ReturnType<typeof setInterval> | undefined;

    const poll = async (opts?: { initial?: boolean; afterId?: number; full?: boolean }) => {
      if (generation !== pollGenerationRef.current) return;
      if (inFlightRef.current || document.hidden) return;

      const codes = activeChannelCodesRef.current.filter((code) => code !== INFO_CHAT_CHANNEL);
      if (codes.length === 0) {
        if (!showInfoChannel) {
          setMessages([]);
          lastMessageIdRef.current = undefined;
        }
        return;
      }

      inFlightRef.current = true;
      if (opts?.initial) setLoading(true);
      try {
        const limit = openRef.current ? 80 : STRIP_LINES;
        const data = await chatApi.getMessages({
          channels: codes,
          limit,
          afterId: opts?.full ? undefined : opts?.afterId,
        });
        if (generation !== pollGenerationRef.current) return;

        if (opts?.afterId != null && !opts?.full) {
          setMessages((prev) => {
            const merged = [...prev];
            for (const msg of data) {
              if (!merged.some((m) => m.id === msg.id)) merged.push(msg);
            }
            const trimmed = merged.slice(-120);
            lastMessageIdRef.current = trimmed[trimmed.length - 1]?.id;
            return trimmed;
          });
        } else {
          setMessages(data);
          lastMessageIdRef.current = data[data.length - 1]?.id;
        }
        setError(null);
      } catch (e) {
        if (opts?.initial) {
          setError(e instanceof Error ? e.message : "Failed to load chat");
        }
      } finally {
        inFlightRef.current = false;
        if (opts?.initial) setLoading(false);
      }
    };

    const schedule = () => {
      const interval = open ? POLL_MS_OPEN : POLL_MS_STRIP;
      timer = setInterval(() => {
        void poll({ afterId: lastMessageIdRef.current });
      }, interval);
    };

    void poll({ initial: true });
    schedule();

    const onVisibility = () => {
      if (!document.hidden) {
        void poll({ afterId: lastMessageIdRef.current });
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      pollGenerationRef.current += 1;
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user, channels.length, pollChannelCodes.join(","), open, showInfoChannel]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [open, displayMessages.length]);

  useEffect(() => {
    if (writableChannels.some((c) => c.code === composeChannel)) return;
    if (writableChannels.length > 0) setComposeChannel(writableChannels[0].code);
  }, [writableChannels, composeChannel]);

  const sendMessage = async () => {
    const text = draft.trim();
    if (!text || !composeChannel || composeChannel === INFO_CHAT_CHANNEL) return;
    setSending(true);
    setError(null);
    try {
      const msg = await chatApi.postMessage(composeChannel, text);
      setDraft("");
      setMessages((prev) => {
        const merged = [...prev.filter((m) => m.id !== msg.id), msg].slice(-120);
        lastMessageIdRef.current = merged[merged.length - 1]?.id;
        return merged;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  if (!user || channels.length === 0) return null;

  const stripMessages = displayMessages.slice(-STRIP_LINES);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="relative z-0 border-t border-kingdom-mid bg-kingdom-purple/95 px-2 py-1.5 text-left w-full shrink-0 flex items-start gap-2"
      >
        <span className="text-base leading-none shrink-0 pt-0.5" aria-hidden>
          💬
        </span>
        <div className="flex-1 min-w-0">
          {activeChannelCodes.length === 0 ? (
            <p className="text-[11px] text-gray-500 italic leading-snug">All channels hidden</p>
          ) : stripMessages.length === 0 ? (
            <p className="text-[11px] text-gray-500 italic leading-snug">No messages yet</p>
          ) : (
            <div className="space-y-0.5">
              {stripMessages.map((msg) => (
                <p
                  key={msg.id}
                  className={clsx(
                    "text-[11px] truncate leading-snug",
                    msg.channel_code === GUILD_CHAT_CHANNEL ? "text-emerald-300" : "text-gray-300",
                  )}
                >
                  <span className={clsx("font-semibold", messageNameClass(msg.channel_code))}>
                    {msg.display_name}
                  </span>
                  {channels.length > 1 && showChannelTag(msg.channel_code) && (
                    <span className={messageTagClass(msg.channel_code)}> [{msg.channel_name}]</span>
                  )}
                  : {msg.text}
                </p>
              ))}
            </div>
          )}
        </div>
      </button>

      {open && (
        <ModalBackdrop onClose={() => setOpen(false)} className="z-50 items-end bg-black/70 p-3 pb-20">
          <div className="w-full max-w-md bg-kingdom-purple border border-kingdom-light rounded-2xl flex flex-col max-h-[min(78vh,560px)]">
            <div className="flex items-center justify-between px-4 py-3 border-b border-kingdom-mid">
              <h3 className="font-bold text-white font-game">💬 Chat</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-gray-400 text-xl leading-none">
                ✕
              </button>
            </div>

            <div className="px-3 py-2 border-b border-kingdom-mid flex flex-wrap gap-1.5">
              <ChannelPill
                label="All"
                active={allEnabled}
                onClick={toggleAll}
                title={allEnabled ? "Hide all channels" : "Show all channels"}
              />
              {channels.map((channel) => (
                <ChannelPill
                  key={channel.code}
                  label={channel.name}
                  active={enabledCodes.has(channel.code)}
                  onClick={() => toggleChannel(channel.code)}
                  title={
                    channel.code === INFO_CHAT_CHANNEL
                      ? enabledCodes.has(channel.code)
                        ? "Hide system info"
                        : "Show system info"
                      : enabledCodes.has(channel.code)
                        ? `Hide ${channel.name}`
                        : `Show ${channel.name}`
                  }
                />
              ))}
            </div>

            <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto px-3 py-2 space-y-2">
              {loading && messages.length === 0 && (
                <p className="text-center text-gray-500 text-sm py-6 animate-pulse">Loading...</p>
              )}
              {activeChannelCodes.length === 0 && (
                <p className="text-center text-gray-500 text-sm py-6">
                  Enable at least one channel to see messages.
                </p>
              )}
              {activeChannelCodes.length > 0 && displayMessages.length === 0 && !loading && (
                <p className="text-center text-gray-500 text-sm py-6">No messages yet.</p>
              )}
              {displayMessages.map((msg) => (
                <div key={msg.id} className="text-sm leading-snug">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className={clsx("font-semibold", messageNameClass(msg.channel_code))}>
                      {msg.display_name}
                    </span>
                    {channels.length > 1 && showChannelTag(msg.channel_code) && (
                      <span
                        className={clsx(
                          "text-[10px] uppercase tracking-wide",
                          messageTagClass(msg.channel_code),
                        )}
                      >
                        {msg.channel_name}
                      </span>
                    )}
                    <span className="text-[10px] text-gray-600 ml-auto">{formatTime(msg.created_at)}</span>
                  </div>
                  <p className={clsx("break-words", messageBodyClass(msg.channel_code))}>{msg.text}</p>
                </div>
              ))}
            </div>

            {error && (
              <div className="mx-3 mb-2 bg-rose-900/30 border border-rose-500 rounded-lg px-2 py-1.5 text-rose-300 text-xs">
                {error}
              </div>
            )}

            <div className="border-t border-kingdom-mid p-3 space-y-2">
              {writableChannels.length > 1 && (
                <div className="flex gap-1 flex-wrap">
                  {writableChannels.map((c) => (
                      <button
                        key={c.code}
                        type="button"
                        onClick={() => setComposeChannel(c.code)}
                        className={clsx(
                          "text-[10px] px-2 py-0.5 rounded-full border",
                          composeChannel === c.code
                            ? "border-kingdom-glow text-kingdom-glow bg-kingdom-mid/60"
                            : "border-gray-600 text-gray-400",
                        )}
                      >
                        → {c.name}
                      </button>
                    ))}
                </div>
              )}
              <div className="flex gap-2">
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  maxLength={500}
                  disabled={sending || writableChannels.length === 0}
                  placeholder={
                    writableChannels.length === 0
                      ? "Enable a writable channel to chat..."
                      : "Type a message..."
                  }
                  className="flex-1 bg-kingdom-dark border border-kingdom-mid rounded-xl px-3 py-2 text-sm text-white placeholder:text-gray-500 disabled:opacity-50"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void sendMessage();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => void sendMessage()}
                  disabled={sending || !draft.trim() || writableChannels.length === 0}
                  className="px-4 py-2 rounded-xl bg-kingdom-glow text-black font-bold text-sm disabled:opacity-50"
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        </ModalBackdrop>
      )}
    </>
  );
}

function ChannelPill({
  label,
  active,
  onClick,
  title,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  title?: string;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={clsx(
        "text-xs px-2.5 py-1 rounded-full border transition-colors",
        active
          ? "border-kingdom-glow text-kingdom-glow bg-kingdom-mid/50"
          : "border-gray-600 text-gray-500 bg-kingdom-dark/40 line-through opacity-60",
      )}
    >
      {label}
    </button>
  );
}
